import React from 'react';
import { DataTable, TableBody } from '@/components/ui/data-table';
import type { DataTableColumn } from '@/components/ui/data-table/DataTableContext';
import { formatDate, formatOfferId } from '@/utils/formatters';
import OfferPageRowActions from './OfferPageRowActions';
import OfferStatusBadge from './OfferStatusBadge';
import { Badge } from '@/components/ui/badge';
import { useModal } from '@/contexts/ModalContext';

interface OfferPage {
  id: string;
  heading?: string;
  title?: string;
  subheading?: string;
  slug: string;
  status?: string;
  created_at: string;
  createdAt?: string;
  user_id: string;
  unique_id?: string;
  products?: Array<{ product_id: string; sort_order: number }>;
}

interface OfferPageTableProps {
  offerPages: OfferPage[];
  onArchive: (id: string) => void;
  onCopyLink: (id: string) => void;
  onCopyId: (id: string) => void;
  onEdit?: (id: string) => void;
  filterStatus?: string;
  searchQuery?: string;
}

const OfferPageTable: React.FC<OfferPageTableProps> = ({ 
  offerPages, 
  onArchive, 
  onCopyLink, 
  onCopyId,
  onEdit,
  filterStatus = 'all',
  searchQuery = ''
}) => {
  const { openModal: _openModal } = useModal();

  const columns: DataTableColumn[] = [
    { id: 'name', label: 'Name', align: 'left' },
    { id: 'products', label: 'Products', align: 'left' },
    { id: 'created', label: 'Created', align: 'left' },
    { id: 'status', label: 'Status', align: 'left' },
    //{ id: 'id', label: 'ID', align: 'left' },
    { id: 'actions', label: ' Actions', align: 'center'  },
  ];

  return (
    <>
      <DataTable className="bg-background">
        <DataTable.Header columns={columns} />
        {offerPages.length === 0 ? (
          <DataTable.Empty colSpan={6} message="No bundles found matching your filters" />
        ) : (
          <TableBody>
            {offerPages.map((page) => {
              const displayTitle = page.title || page.heading || "Unnamed Bundle";
              
              return (
                <DataTable.Row 
                  key={page.id} 
                  entityType="bundle"
                  entityId={page.id}
                >
                  <DataTable.Cell className="font-medium">{displayTitle}</DataTable.Cell>
                  <DataTable.Cell>
                    <Badge variant="secondary" className="font-normal">
                      {page.products?.length || 0} products
                    </Badge>
                  </DataTable.Cell>
                  <DataTable.Cell>{page.createdAt ? formatDate(page.createdAt) : formatDate(page.created_at)}</DataTable.Cell>
                  <DataTable.Cell>
                    <OfferStatusBadge status={page.status} />
                  </DataTable.Cell>
                  <DataTable.Actions>
                    <OfferPageRowActions
                      pageId={page.id}
                      status={page.status}
                      onCopyLink={onCopyLink}
                      onArchive={onArchive}
                      onEdit={onEdit}
                    />
                  </DataTable.Actions>
                </DataTable.Row>
              );
            })}
          </TableBody>
        )}
      </DataTable>
    </>
  );
};

export default OfferPageTable;